import React, { useState, useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { useDepartments } from "../../../hooks/useDepartments";
import { ongoingProjectService } from "../../../services/ongoingProjectService";
import { ongoingProjectSubmissionService } from "../../../services/ongoingProjectSubmissionService";
import LoadingSpinner from "../../common/LoadingSpinner";
import { EmptyState } from "../../common/EmptyState";
import UniversalFilters from "../../common/UniversalFilters";
import Button from "../../ui/Button";
import ExportExcelButton from "../../common/ExportExcelButton";
import { getFilterConfig } from "../../../constants/filterConfigs";
import { useFilters } from "../../../hooks/useFilters";
import { formatOngoingProjectAmount } from "../../../constants/ongoingProjectConstants";
import {
  financialYearUtils,
  arrayUtils,
  dateUtils,
} from "../../../utils/helpers";
import { USER_ROLES } from "../../../constants/authConstants";
import { exportOngoingProjectsPDF } from "../../../utils/pdfExport";
import { exportOngoingProjectsToExcel } from "../../../utils/excelExport";

const OngoingProjectDisplay = () => {
  const { user, getUserRole, getUserDisplayName } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const {
    departments,
    loading: loadingDepartments,
    getDepartmentLabel,
  } = useDepartments();

  const isAdmin = getUserRole() === USER_ROLES.ADMIN;

  // State variables
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [submissionStatuses, setSubmissionStatuses] = useState({});
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [lockedDepartments, setLockedDepartments] = useState([]);

  const [deletingProject, setDeletingProject] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  // ⭐ NEW: Unlock state
  const [unlockingDepartment, setUnlockingDepartment] = useState(null);

  const { filters, handleFilterChange, clearFilters, hasActiveFilters } =
    useFilters({
      search: "",
      department: "",
      financialYear: "",
      yearOfCommencement: "",
    });

  const filterConfig = getFilterConfig("ONGOING_PROJECTS", {
    showDepartmentFilter: true,
  });

  const titleSize = getResponsiveValue({
    mobile: "text-xl",
    desktop: "text-2xl",
  });

  const cardPadding = getResponsiveValue({
    mobile: "p-4",
    desktop: "p-6",
  });

  // useEffect hooks
  useEffect(() => {
    loadProjects();
  }, [filters.financialYear]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [projects, filters]);

  useEffect(() => {
    if (projects.length > 0) {
      loadSubmissionStatuses();
    }
  }, [projects]);

  // Load projects and submissions together - OPTIMIZED
  const loadProjects = async () => {
    try {
      setLoading(true);
      setError("");

      const result = await ongoingProjectService.getAllOngoingProjects();

      if (result.success) {
        let allProjects = result.data;

        if (isAdmin && filters.financialYear) {
          const lockedDepts =
            await ongoingProjectSubmissionService.getLockedDepartments(
              filters.financialYear,
            );
          setLockedDepartments(lockedDepts.data);

          allProjects = allProjects.filter((project) => {
            return (
              project.financialYear === filters.financialYear &&
              lockedDepts.data.includes(project.department)
            );
          });
        }

        setProjects(allProjects);
        
        // Load submission statuses immediately after setting projects
        // This prevents the delay in showing status rows
        if (allProjects.length > 0) {
          await loadSubmissionStatusesForProjects(allProjects);
        }
      }
    } catch (error) {
      console.error("Error loading ongoing projects:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Load submission statuses inline with projects data
  const loadSubmissionStatusesForProjects = async (projectsData) => {
    try {
      setLoadingSubmissions(true);

      const combinations = new Set();
      projectsData.forEach((project) => {
        if (project.department && project.financialYear) {
          const key = `${project.department}|${project.financialYear}`;
          combinations.add(key);
        }
      });

      const statuses = {};
      
      // Use Promise.all for parallel fetching - much faster!
      const fetchPromises = Array.from(combinations).map(async (combo) => {
        const [dept, fy] = combo.split("|");
        
        try {
          const result =
            await ongoingProjectSubmissionService.getSubmissionStatus(dept, fy);
          if (result.data) {
            const statusKey = `${dept}-${fy}`;
            statuses[statusKey] = result.data;
          }
        } catch (error) {
          console.error(`Error loading submission for ${combo}:`, error);
        }
      });

      // Wait for all status fetches to complete in parallel
      await Promise.all(fetchPromises);

      setSubmissionStatuses(statuses);
    } catch (error) {
      console.error("Error loading submission statuses:", error);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  // Load submission statuses - FINAL FIXED VERSION
  const loadSubmissionStatuses = async () => {
    try {
      setLoadingSubmissions(true);

      const combinations = new Set();
      projects.forEach((project) => {
        if (project.department && project.financialYear) {
          // Store department and FY separately to avoid split issues
          const key = `${project.department}|${project.financialYear}`;
          combinations.add(key);
          console.log('📌 Adding combination:', key, 'from project:', {
            id: project.id,
            dept: project.department,
            fy: project.financialYear
          });
        }
      });

      console.log('🔍 Loading submission statuses for:', Array.from(combinations));

      const statuses = {};
      for (const combo of combinations) {
        // Split by pipe instead of dash to handle dept codes with dashes
        const [dept, fy] = combo.split("|");
        console.log('🔎 Fetching status for dept:', dept, 'fy:', fy);
        
        try {
          const result =
            await ongoingProjectSubmissionService.getSubmissionStatus(dept, fy);
          if (result.data) {
            // Store with pipe separator to match getSubmissionStatus lookup
            const statusKey = `${dept}-${fy}`;
            statuses[statusKey] = result.data;
            console.log('✅ Status loaded for', statusKey, ':', {
              isLocked: result.data.isLocked,
              submittedAt: result.data.submittedAt
            });
          } else {
            console.log('⚠️ No submission data for', combo);
          }
        } catch (error) {
          console.error(`❌ Error loading submission for ${combo}:`, error);
        }
      }

      console.log('📊 Final submission statuses:', statuses);
      console.log('📊 Status keys:', Object.keys(statuses));
      setSubmissionStatuses(statuses);
    } catch (error) {
      console.error("Error loading submission statuses:", error);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  // Get submission status helper
  const getSubmissionStatus = (department, financialYear) => {
    const key = `${department}-${financialYear}`;
    return submissionStatuses[key] || null;
  };

  // Apply filters and sort
  const applyFiltersAndSort = () => {
    let filtered = [...projects];

    if (filters.search && filters.search.trim()) {
      const searchFields = ["projectName", "department"];
      filtered = arrayUtils.filterBySearch(
        filtered,
        filters.search,
        searchFields,
      );
    }

    if (filters.department) {
      filtered = filtered.filter(
        (project) => project.department === filters.department,
      );
    }

    if (filters.financialYear) {
      filtered = filtered.filter(
        (project) => project.financialYear === filters.financialYear,
      );
    }

    if (filters.yearOfCommencement) {
      filtered = filtered.filter(
        (project) =>
          project.yearOfCommencement?.toString() === filters.yearOfCommencement,
      );
    }

    filtered = arrayUtils.sortBy(filtered, "department", "asc");

    setFilteredProjects(filtered);
  };

  // ⭐ NEW: Handle unlock department submission
  const handleUnlockDepartment = async (department, financialYear, departmentName) => {
    if (!isAdmin) {
      alert("Only administrators can unlock submissions.");
      return;
    }

    if (
      !confirm(
        `Are you sure you want to unlock the ongoing projects submission for ${departmentName}?\n\nThis will allow the department to:\n- Edit existing projects\n- Add new projects\n- Delete projects\n- Change project data\n\nFinancial Year: ${financialYear}`
      )
    ) {
      return;
    }

    try {
      setUnlockingDepartment(department);
      
      const result = await ongoingProjectSubmissionService.unlockOngoingProjects(
        department,
        financialYear,
        user.uid,
        getUserDisplayName()
      );

      if (result.success) {
        setSuccessMessage(
          `${departmentName} submission unlocked successfully. They can now edit their ongoing projects.`
        );
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 5000);

        // Reload submission statuses and projects
        await loadSubmissionStatuses();
        await loadProjects();
      }
    } catch (error) {
      console.error("Error unlocking department submission:", error);
      alert("Failed to unlock submission: " + error.message);
    } finally {
      setUnlockingDepartment(null);
    }
  };

  // Handle delete project
  const handleDeleteProject = async (projectId, projectName) => {
    if (!isAdmin) {
      alert("Only administrators can delete projects.");
      return;
    }

    if (
      !confirm(
        `Are you sure you want to delete "${projectName}"?\n\nThis action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      setDeletingProject(projectId);
      const result =
        await ongoingProjectService.deleteOngoingProject(projectId);

      if (result.success) {
        setProjects((prev) => prev.filter((p) => p.id !== projectId));
        setSuccessMessage(`Project "${projectName}" deleted successfully.`);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      alert("Failed to delete project: " + error.message);
    } finally {
      setDeletingProject(null);
    }
  };

  // Handle export PDF
  const handleExport = () => {
    try {
      setExporting(true);
      exportOngoingProjectsPDF(filteredProjects, null, filters);
      setShowSuccessMessage(true);
      setSuccessMessage("PDF exported successfully!");
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      alert("Failed to export PDF: " + error.message);
    } finally {
      setExporting(false);
    }
  };

  // Handle export Excel
  const handleExportExcel = () => {
    try {
      setExportingExcel(true);

      const exportData = {
        projects: filteredProjects,
        grandTotals: grandTotals,
      };

      exportOngoingProjectsToExcel(exportData, filters, getDepartmentLabel);
      setShowSuccessMessage(true);
      setSuccessMessage("Excel exported successfully!");
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error("Error exporting Excel:", error);
      alert("Failed to export Excel: " + error.message);
    } finally {
      setExportingExcel(false);
    }
  };

  // Helper functions
  const getGroupedProjects = () => {
    const grouped = {};
    filteredProjects.forEach((project) => {
      const dept = project.department;
      if (!grouped[dept]) {
        grouped[dept] = [];
      }
      grouped[dept].push(project);
    });
    return grouped;
  };

  const calculateDepartmentTotals = (projects) => {
    return {
      totalEstimatedCost: projects.reduce(
        (sum, p) => sum + (parseFloat(p.totalEstimatedCost) || 0),
        0,
      ),
      totalExpenditure: projects.reduce(
        (sum, p) => sum + (parseFloat(p.totalExpenditure) || 0),
        0,
      ),
      balanceRemaining: projects.reduce(
        (sum, p) => sum + (parseFloat(p.balanceRemaining) || 0),
        0,
      ),
      projectCount: projects.length,
    };
  };

  const grandTotals = calculateDepartmentTotals(filteredProjects);
  const groupedProjects = getGroupedProjects();
  const departmentKeys = Object.keys(groupedProjects).sort();

  // Loading state
  if (loading || loadingDepartments) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading ongoing projects..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {showSuccessMessage && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 shadow-sm">
          <div className="flex items-center">
            <svg
              className="w-4 h-4 text-green-500 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-green-700 font-medium text-sm">
              {successMessage}
            </span>
          </div>
        </div>
      )}

      {/* Info Banner */}
      {isAdmin && filters.financialYear && lockedDepartments.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
          <div className="flex items-center">
            <svg
              className="w-4 h-4 text-blue-500 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-blue-700 text-sm">
              Showing ongoing projects only from departments with locked
              submissions ({lockedDepartments.length}{" "}
              {lockedDepartments.length === 1 ? "department" : "departments"})
            </span>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${titleSize} font-bold text-text-primary mb-2`}>
            Ongoing Projects Overview
          </h1>
          <p className="text-text-secondary">
            {isAdmin
              ? "View ongoing projects from departments with locked submissions"
              : "View all ongoing projects across departments"}
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <ExportExcelButton
            onClick={handleExportExcel}
            loading={exportingExcel}
            disabled={filteredProjects.length === 0}
          />

          <Button
            onClick={handleExport}
            size="md"
            variant="outline"
            className="flex items-center space-x-2"
            disabled={exporting || filteredProjects.length === 0}
            loading={exporting}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span>{exporting ? "Exporting..." : "Export PDF"}</span>
          </Button>

          <UniversalFilters
            config={filterConfig}
            values={filters}
            onChange={handleFilterChange}
            onClearFilters={clearFilters}
            mode="dropdown"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-error-subtle border border-border-error rounded-xl p-4">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-error mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-error font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Table Container */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {filteredProjects.length === 0 ? (
          <div className={cardPadding}>
            <EmptyState
              icon={
                <svg
                  className="w-16 h-16 text-text-quaternary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              }
              title={
                projects.length === 0
                  ? "No ongoing projects found"
                  : "No projects match your filters"
              }
              description={
                projects.length === 0
                  ? isAdmin
                    ? "Ongoing projects will appear here once departments lock and submit their data"
                    : "No ongoing projects have been added yet"
                  : "Try adjusting your filter criteria to see more projects"
              }
              actionLabel={hasActiveFilters ? "Clear Filters" : null}
              onAction={hasActiveFilters ? clearFilters : null}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-16">
                    S. No.
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-32">
                    Department
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-48">
                    Project Name
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-32">
                    Year of Commencement
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-32">
                    Total Approved Cost
                    <br />
                    <span className="text-gray-500 normal-case font-normal">
                      (in lakh)
                    </span>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-32">
                    Total Clearance
                    <br />
                    <span className="text-gray-500 normal-case font-normal">
                      (in lakh)
                    </span>
                    <br />
                    <span className="text-gray-500 normal-case font-normal">
                      (including CFY)
                    </span>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-32">
                    Balance Remaining
                    <br />
                    <span className="text-gray-500 normal-case font-normal">
                      (in lakh)
                    </span>
                  </th>
                  <th
                    className={`px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider min-w-48 ${
                      isAdmin ? "border-r border-gray-200" : ""
                    }`}
                  >
                    Remarks
                  </th>
                  {isAdmin && (
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-16">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {/* Table Body - Generate rows */}
                {(() => {
                  let serialCounter = 1;
                  const rows = [];

                  departmentKeys.forEach((department) => {
                    const projects = groupedProjects[department];
                    const firstProject = projects[0];
                    const submissionStatus = firstProject
                      ? getSubmissionStatus(
                          department,
                          firstProject.financialYear,
                        )
                      : null;

                    // Department Status Row with Unlock Button
                    if (submissionStatus) {
                      rows.push(
                        <tr
                          key={`status-${department}`}
                          className={`${
                            submissionStatus.isLocked
                              ? "bg-green-50"
                              : "bg-blue-50"
                          }`}
                        >
                          <td colSpan={isAdmin ? 9 : 8} className="px-3 py-2">
                            <div className="flex items-center justify-between">
                              {/* Left side - Status info */}
                              <div className="flex items-center space-x-2">
                                {submissionStatus.isLocked ? (
                                  <>
                                    <svg
                                      className="w-4 h-4 text-green-600"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    <span className="text-xs font-medium text-green-800">
                                      {getDepartmentLabel(department)} - Locked &
                                      Submitted on{" "}
                                      {dateUtils.formatDate(
                                        submissionStatus.submittedAt,
                                      )}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <svg
                                      className="w-4 h-4 text-blue-600"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
                                    </svg>
                                    <span className="text-xs font-medium text-blue-800">
                                      {getDepartmentLabel(department)} - Draft
                                      (Not Submitted)
                                    </span>
                                  </>
                                )}
                              </div>

                              {/* Right side - Unlock button (admin only, if locked) */}
                              {isAdmin && submissionStatus.isLocked && (
                                <button
                                  onClick={() => handleUnlockDepartment(
                                    department,
                                    firstProject.financialYear,
                                    getDepartmentLabel(department)
                                  )}
                                  disabled={unlockingDepartment === department}
                                  className="px-3 py-1 text-xs font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                  title="Unlock submission for department"
                                >
                                  {unlockingDepartment === department ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-700"></div>
                                      <span>Unlocking...</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                                        />
                                      </svg>
                                      <span>Unlock</span>
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>,
                      );
                    }

                    // Project Rows for this department
                    projects.forEach((project) => {
                      rows.push(
                        <tr
                          key={project.id}
                          className="hover:bg-blue-50 transition-colors duration-200"
                        >
                          <td className="px-3 py-3 text-xs text-gray-600 font-semibold border-r border-gray-200">
                            <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-xs font-bold">
                              {serialCounter++}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-xs text-gray-900 font-semibold border-r border-gray-200 text-center">
                            {getDepartmentLabel(project.department)}
                          </td>
                          <td className="px-3 py-3 text-xs text-gray-900 font-medium border-r border-gray-200">
                            <div className="max-w-xs">
                              <div className="font-medium line-clamp-2">
                                {project.projectName}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center text-xs text-gray-600 border-r border-gray-200">
                            {project.yearOfCommencement || "N/A"}
                          </td>
                          <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium border-r border-gray-200">
                            {formatOngoingProjectAmount(
                              project.totalEstimatedCost,
                            )}
                          </td>
                          <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium border-r border-gray-200">
                            {formatOngoingProjectAmount(
                              project.totalExpenditure,
                            )}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-medium border-r border-gray-200">
                            <span className="text-blue-600">
                              {formatOngoingProjectAmount(
                                project.balanceRemaining,
                              )}
                            </span>
                          </td>
                          <td
                            className={`px-3 py-3 text-xs text-gray-600 ${
                              isAdmin ? "border-r border-gray-200" : ""
                            }`}
                          >
                            <div className="max-w-xs">
                              <div className="line-clamp-2 text-left">
                                {project.remarks || "—"}
                              </div>
                            </div>
                          </td>
                          {isAdmin && (
                            <td className="px-3 py-3 text-center">
                              <button
                                onClick={() =>
                                  handleDeleteProject(
                                    project.id,
                                    project.projectName,
                                  )
                                }
                                disabled={deletingProject === project.id}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Delete project"
                              >
                                {deletingProject === project.id ? (
                                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-red-600"></div>
                                ) : (
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                )}
                              </button>
                            </td>
                          )}
                        </tr>,
                      );
                    });

                    // Department Total Row
                    const deptTotals = calculateDepartmentTotals(projects);
                    rows.push(
                      <tr
                        key={`total-${department}`}
                        className="bg-gray-700 text-white font-bold border-t-2 border-gray-600"
                      >
                        <td className="px-3 py-3 border-r border-gray-600"></td>
                        <td className="px-3 py-3 text-xs font-bold border-r border-gray-600">
                          Total - {getDepartmentLabel(department)} (
                          {deptTotals.projectCount} projects)
                        </td>
                        <td className="px-3 py-3 border-r border-gray-600"></td>
                        <td className="px-3 py-3 border-r border-gray-600"></td>
                        <td className="px-3 py-3 text-center text-xs font-bold border-r border-gray-600">
                          {formatOngoingProjectAmount(
                            deptTotals.totalEstimatedCost,
                          )}
                        </td>
                        <td className="px-3 py-3 text-center text-xs font-bold border-r border-gray-600">
                          {formatOngoingProjectAmount(
                            deptTotals.totalExpenditure,
                          )}
                        </td>
                        <td className="px-3 py-3 text-center text-xs font-bold border-r border-gray-600">
                          {formatOngoingProjectAmount(
                            deptTotals.balanceRemaining,
                          )}
                        </td>
                        <td
                          className={`px-3 py-3 ${
                            isAdmin ? "border-r border-gray-600" : ""
                          }`}
                        ></td>
                        {isAdmin && <td className="px-3 py-3"></td>}
                      </tr>,
                    );
                  });

                  return rows;
                })()}

                {/* Grand Total Row */}
                {filteredProjects.length > 0 && (
                  <tr className="bg-gray-800 text-white font-bold border-t-4 border-gray-700">
                    <td className="px-3 py-3 border-r border-gray-700"></td>
                    <td className="px-3 py-3 text-xs font-bold border-r border-gray-700">
                      Grand Total ({grandTotals.projectCount} projects)
                      {hasActiveFilters && " (Filtered)"}
                    </td>
                    <td className="px-3 py-3 border-r border-gray-700"></td>
                    <td className="px-3 py-3 border-r border-gray-700"></td>
                    <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-700">
                      {formatOngoingProjectAmount(
                        grandTotals.totalEstimatedCost,
                      )}
                    </td>
                    <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-700">
                      {formatOngoingProjectAmount(grandTotals.totalExpenditure)}
                    </td>
                    <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-700">
                      {formatOngoingProjectAmount(grandTotals.balanceRemaining)}
                    </td>
                    <td
                      className={`px-3 py-3 ${
                        isAdmin ? "border-r border-gray-700" : ""
                      }`}
                    ></td>
                    {isAdmin && <td className="px-3 py-3"></td>}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OngoingProjectDisplay;